<template>
  <!-- 회원 상세 (docs/architecture/admin-member-management-design.md 3절): 머리 + 좌(특이사항·메모/연락) 우(수강권·출석) + 맨 아래 기본 정보·대시보드 -->
  <div class="profile-content">
    <!-- 머리: 이름·전화번호·가입일(D+n)·앱 가입 전 -->
    <div class="profile-head">
      <div class="head-info">
        <h1 class="member-name">{{ profile.name }}</h1>
        <span v-if="phoneText" class="member-phone">{{ phoneText }}</span>
        <span v-if="joinDateText" class="member-join">
          가입 {{ joinDateText }}
          <span v-if="joinDays !== null" class="join-days"
            >(D+{{ joinDays }})</span
          >
        </span>
        <span v-if="profile.app_signed_up === false" class="head-badge muted"
          >앱 가입 전</span
        >
      </div>
      <button class="add-membership-btn" @click="openMembershipModal">
        회원권 추가
      </button>
    </div>

    <!-- 프로필(특이사항)과 수강권 목록(휴회 이력까지)을 읽기 전에는 아래 카드들을 그리지 않고 이 한 줄만 보인다(bodyReady). 예전에는 한 줄짜리
         특이사항 자리 아래에 메모·연락 카드와 [+ 연락]을 먼저 그려, 여러 줄 특이사항이 오면 220~330px 밀려 내려가 누르려던 자리에 다른 것이
         왔다(4차 리뷰). 좁은 화면에서는 휴회 이력·수강권이 늦게 와도 같은 일이 생겼다(5차 리뷰). 아래 카드들은 v-show라 그동안에도 만들어져
         기록·수강권·출석을 함께 읽는다 -->
    <section v-if="!bodyReady" class="card body-loading">
      <p class="note-placeholder">불러오는 중...</p>
    </section>

    <!-- 좁은 화면(≤ 1100px)에서는 .profile-col이 풀려 특이사항 → 수강권 → 메모·연락 → 출석 순으로 쌓인다 -->
    <div v-show="bodyReady" class="profile-columns">
      <div class="profile-col">
        <!-- 특이사항 (users.admin_note): 이 칸만 따로 고치고 admin_note만 보낸다 -->
        <section
          class="card note-card slot-note"
          :class="{ 'has-note': profileLoaded && !!noteText }"
        >
          <div class="card-head">
            <h2 class="card-title">
              <!-- 노란 "!"는 특이사항이 있을 때만(왼쪽 노란 띠와 같은 조건). 비었으면 "특이사항 없음"만(설계 3절, 6차 리뷰: 모든 회원에게 떠
                   특이사항이 있는 회원이 눈에 띄지 않았다) -->
              <span
                v-if="profileLoaded && noteText"
                class="note-mark"
                aria-hidden="true"
                >!</span
              >
              특이사항
            </h2>
            <div class="card-actions">
              <!-- 프로필을 읽기 전·읽지 못했을 때는 수정할 수 없다(화면 기본값을 저장하면 회원 값을 비움) -->
              <button
                v-if="!editingNote"
                class="edit-max-hr-btn"
                :disabled="!profileLoaded"
                @click="startEditNote"
              >
                수정
              </button>
              <!-- [취소][저장]: 바로 아래 메모·연락 폼, 연락 기록 모달과 같은 순서(3차 리뷰) -->
              <template v-else>
                <button
                  class="max-hr-cancel-btn"
                  :disabled="savingNote"
                  @click="requestCancelNote"
                >
                  취소
                </button>
                <button
                  class="max-hr-save-btn"
                  :disabled="savingNote"
                  @click="saveNote"
                >
                  저장
                </button>
              </template>
            </div>
          </div>

          <textarea
            v-if="editingNote"
            ref="noteTextarea"
            v-model="noteDraft"
            rows="4"
            class="extra-input"
            placeholder="부상·주의사항 등 코치가 늘 알아야 할 내용을 입력하세요"
          />
          <p v-else-if="!profileLoaded" class="note-placeholder">
            {{
              profileLoadFailed
                ? "프로필을 불러오지 못했습니다."
                : "불러오는 중..."
            }}
          </p>
          <p v-else-if="noteText" class="note-text">{{ noteText }}</p>
          <p v-else class="note-placeholder">특이사항 없음</p>
        </section>

        <!-- 메모·연락 기록 -->
        <MemberNotes
          ref="memberNotes"
          class="slot-notes"
          :user-id="targetUserId"
        />
      </div>

      <div class="profile-col">
        <!-- 수강권 (회원권 관리) -->
        <section class="card membership-card slot-mem">
          <div class="card-head">
            <h2 class="card-title">
              수강권
              <span
                v-if="!loadingMemberships && !membershipsError"
                class="title-count"
                >{{ memberships.length }}개</span
              >
            </h2>
            <div class="mem-tabs">
              <button
                :class="['mem-tab', { active: memTab === 'active' }]"
                @click="
                  memTab = 'active';
                  loadMemberships();
                "
              >
                활성
              </button>
              <button
                :class="['mem-tab', { active: memTab === 'all' }]"
                @click="
                  memTab = 'all';
                  loadMemberships();
                "
              >
                전체
              </button>
            </div>
          </div>

          <div v-if="loadingMemberships" class="empty-state">
            <p>수강권을 불러오는 중...</p>
          </div>
          <!-- 못 읽었으면 "수강권이 없습니다"가 아니라 실패를 보인다. 이 목록이 지금 수강권을 읽는 유일한 곳이다(3차 리뷰) -->
          <div v-else-if="membershipsError" class="empty-state mem-error">
            <p>수강권을 불러오지 못했습니다.</p>
            <button type="button" class="edit-mem-btn" @click="loadMemberships">
              다시 시도
            </button>
          </div>
          <div v-else-if="memberships.length === 0" class="empty-state">
            <p>수강권이 없습니다.</p>
          </div>
          <div v-else class="membership-list">
            <div
              v-for="row in membershipRows"
              :key="row.mem.id"
              class="membership-item"
              :class="'state-' + row.state"
            >
              <div class="mem-info">
                <div class="mem-type-row">
                  <span
                    class="mem-dot"
                    :class="row.state"
                    aria-hidden="true"
                  ></span>
                  <span class="mem-type">{{ row.mem.type }}</span>
                  <span class="mem-state" :class="row.state">{{
                    membershipStateLabel(row.state)
                  }}</span>
                  <span
                    v-if="row.mem.status === 'cancelled'"
                    class="mem-status cancelled"
                    >취소</span
                  >
                  <span class="mem-count">{{
                    membershipCountText(row.mem)
                  }}</span>
                </div>
                <div class="mem-details">
                  <span
                    >{{ dotDate(row.mem.start_date) }} ~
                    {{ dotDate(row.mem.expired_at) }}</span
                  >
                  <span
                    v-if="row.state === 'in_use' && row.daysLeft !== null"
                    class="mem-days"
                    :class="daysLeftTone(row.daysLeft)"
                    >{{ daysLeftText(row.daysLeft) }}</span
                  >
                  <span v-if="weeklyPassNote(row.mem)" class="mem-week-note">{{
                    weeklyPassNote(row.mem)
                  }}</span>
                </div>
                <!-- 수강권 특이사항(회원권 추가·수정에서 적음, 코치만 봄) -->
                <p v-if="row.mem.note" class="mem-note">{{ row.mem.note }}</p>
              </div>
              <div class="mem-actions">
                <!-- 주2회권: 첫 주에 1회만 채운 회원에게 코치 재량으로 한 주(1회)를 더 준다 -->
                <button
                  v-if="canExtendWeek(row.mem)"
                  class="edit-mem-btn"
                  :disabled="extendingId === row.mem.id"
                  @click="extendWeek(row.mem)"
                >
                  한 주 연장
                </button>
                <button
                  class="edit-mem-btn"
                  @click="openHoldModal(row.mem)"
                  v-if="row.mem.status === 'active'"
                >
                  휴회
                </button>
                <button class="edit-mem-btn" @click="openEditModal(row.mem)">
                  수정
                </button>
              </div>

              <!-- 휴회 이력 -->
              <div
                v-if="row.mem._holds && row.mem._holds.length > 0"
                class="hold-list"
              >
                <div
                  v-for="hold in row.mem._holds"
                  :key="hold.id"
                  class="hold-item"
                >
                  <span class="hold-period"
                    >휴회: {{ dotDate(hold.start_date) }} ~
                    {{ dotDate(hold.end_date) }} ({{ hold.days }}일)</span
                  >
                  <span v-if="hold.reason" class="hold-reason">{{
                    hold.reason
                  }}</span>
                  <div class="hold-actions">
                    <button
                      class="hold-edit-btn"
                      @click="openEditHoldModal(hold)"
                    >
                      수정
                    </button>
                    <button class="hold-delete-btn" @click="deleteHold(hold)">
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 출석 -->
        <section class="card attendance-card slot-att">
          <div class="card-head">
            <h2 class="card-title">출석</h2>
          </div>

          <!-- 0은 색 없이(6차 리뷰: 첫 출석 전 회원이 초록 "출석 0"·빨간 "불참 0"으로 보였다). 출석이 있으면 초록, 불참이 있으면 빨강 -->
          <div class="attendance-summary">
            <span
              >출석
              <strong
                :class="{ attended: profileLoaded && profile.attendance > 0 }"
                >{{ profileLoaded ? profile.attendance : "-" }}</strong
              ></span
            >
            <span class="sep" aria-hidden="true">·</span>
            <span
              >불참
              <strong
                :class="{ absent: profileLoaded && profile.absence > 0 }"
                >{{ profileLoaded ? profile.absence : "-" }}</strong
              ></span
            >
            <template v-if="lastAttendedText">
              <span class="sep" aria-hidden="true">·</span>
              <span
                >최근 <strong>{{ lastAttendedText }}</strong></span
              >
            </template>
          </div>

          <div class="attendance-list" @scroll="onScroll">
            <div
              v-for="record in attendanceRecords"
              :key="record.id"
              class="attendance-item"
            >
              <div
                class="attendance-indicator"
                :class="record.attended ? 'attended' : 'absent'"
              ></div>
              <div class="attendance-info">
                <!-- 날짜(요일까지)와 시각은 각각 한 덩어리: 줄을 바꿔야 하면 날짜와 시각 사이에서만 바꾼다 -->
                <div class="attendance-date">
                  <span class="att-day">{{ formatDate(record.date) }}</span>
                  {{ " " }}<span class="att-time">{{ record.time }}</span>
                </div>
                <div class="attendance-type" :class="getTypeClass(record.type)">
                  {{ typeLabelOf(record) }}
                </div>
              </div>
              <button
                class="attendance-action"
                @click="goToRecords(record.date)"
              >
                기록 보러 가기
              </button>
            </div>

            <!-- 로딩 상태 -->
            <div v-if="loadingAttendance" class="loading-more">
              <p>출석내역을 불러오는 중...</p>
            </div>

            <!-- 더보기 버튼 -->
            <div
              v-if="hasMoreAttendance && !loadingAttendance"
              class="load-more"
            >
              <button @click="loadMoreAttendance" class="load-more-btn">
                더보기
              </button>
            </div>

            <!-- 데이터 끝 알림 -->
            <div
              v-if="
                !hasMoreAttendance &&
                attendanceRecords.length > 0 &&
                !loadingAttendance
              "
              class="end-message"
            >
              <p>모든 출석내역을 불러왔습니다.</p>
            </div>

            <!-- 빈 상태 -->
            <div
              v-if="attendanceRecords.length === 0 && !loadingAttendance"
              class="empty-state"
            >
              <p>출석내역이 없습니다.</p>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- 기본 정보 (생년월일/성별/최대 심박수/몸무게, 한 번에 수정) -->
    <section v-show="bodyReady" class="card basic-card">
      <div class="card-head">
        <h2 class="card-title">기본 정보</h2>
        <div class="card-actions">
          <!-- 프로필을 읽기 전·읽지 못했을 때는 수정할 수 없다(화면 기본값을 저장하면 회원 값을 비움, 출시 최종 확인 3차) -->
          <button
            v-if="!editingProfile"
            class="edit-max-hr-btn"
            :disabled="!profileLoaded"
            @click="startEditProfile"
          >
            수정
          </button>
          <template v-else>
            <button
              class="max-hr-cancel-btn"
              :disabled="savingProfile"
              @click="requestCancelProfile"
            >
              취소
            </button>
            <button
              class="max-hr-save-btn"
              :disabled="savingProfile"
              @click="saveProfileFields"
            >
              저장
            </button>
          </template>
        </div>
      </div>

      <div class="extra-info-grid">
        <div class="extra-field">
          <label class="extra-label">생년월일</label>
          <span v-if="!editingProfile" class="extra-value">{{
            formatBirthDate(profile.birth_date) || "-"
          }}</span>
          <input
            v-else
            v-model="profileEdit.birth_date"
            type="date"
            class="extra-input"
          />
        </div>
        <div class="extra-field">
          <label class="extra-label">성별</label>
          <span v-if="!editingProfile" class="extra-value">{{
            genderLabel(profile.gender)
          }}</span>
          <select v-else v-model="profileEdit.gender" class="extra-input">
            <!-- 성별은 비울 수 없다(서버 400, 출시 최종 확인 2차). "-"는 성별이 없는 옛 행에서 고르기 전 자리로만 -->
            <option v-if="!profile.gender" value="" disabled>-</option>
            <option value="male">남성</option>
            <option value="female">여성</option>
          </select>
        </div>
        <div class="extra-field">
          <label class="extra-label">최대 심박수</label>
          <span v-if="!editingProfile" class="extra-value"
            >{{ profile.max_hr || "-" }} bpm</span
          >
          <input
            v-else
            v-model.number="profileEdit.max_hr"
            type="number"
            min="100"
            max="250"
            step="1"
            class="extra-input"
          />
        </div>
        <div class="extra-field">
          <label class="extra-label">몸무게</label>
          <span v-if="!editingProfile" class="extra-value">
            <template v-if="profile.weight_kg_is_default"
              >미입력 (기본 {{ defaultWeightKg(profile.gender) }}kg
              적용)</template
            >
            <template v-else>{{ profile.weight_kg || "-" }} kg</template>
          </span>
          <input
            v-else
            v-model.number="profileEdit.weight_kg"
            type="number"
            min="20"
            max="300"
            step="0.1"
            class="extra-input"
          />
        </div>
      </div>
    </section>

    <!-- 대시보드 -->
    <section v-show="bodyReady" class="card dashboard-card">
      <div class="card-head">
        <h2 class="card-title">대시보드</h2>
      </div>
      <div class="dash-grid">
        <div class="dash-item">
          <span class="dash-label">누적 Zone 4+</span>
          <span class="dash-value purple">
            {{ Math.floor(dashboard.totalZone4Plus / 60)
            }}<span class="dash-unit">시간</span>
            {{ Math.round(dashboard.totalZone4Plus % 60)
            }}<span class="dash-unit">분</span>
          </span>
        </div>
        <div class="dash-item">
          <span class="dash-label">이번 주 Zone 4+</span>
          <span class="dash-value orange">
            <template v-if="Math.floor(dashboard.weeklyZone4Plus / 60) > 0">
              {{ Math.floor(dashboard.weeklyZone4Plus / 60)
              }}<span class="dash-unit">시간</span>
            </template>
            {{ Math.round(dashboard.weeklyZone4Plus % 60)
            }}<span class="dash-unit">분</span>
          </span>
        </div>
        <div class="dash-item">
          <span class="dash-label">다음 스티커까지</span>
          <span class="dash-value green">
            {{ Math.round(dashboard.minutesToSticker)
            }}<span class="dash-unit">분</span>
          </span>
        </div>
      </div>
    </section>

    <!-- 등록 전 체험 이력: 늦게 오는 카드라 bodyReady 안쪽 맨 아래에 둔다. 예전에는 머리 바로 밑에 있어, 본문이 그려진 뒤 도착한 이력이
         한 줄에서 수백 px로 늘며 아래 카드를 전부 밀었다(특이사항·수강권에서 고친 220~330px 밀림과 같은 증상). 래퍼는 display: contents라
         칸 간격을 늘리지 않고, v-show라 그동안에도 만들어져 프로필·수강권과 함께 읽는다 -->
    <div v-show="bodyReady" class="trial-history-slot">
      <MemberTrialHistory :user-id="targetUserId" />
    </div>
  </div>

  <!-- 회원권 추가 모달 -->
  <div
    v-if="showMembershipModal"
    class="modal-overlay"
    @click="closeMembershipModal"
  >
    <div class="modal" @click.stop>
      <h3 class="modal-title">{{ profile.name }} 회원에게 수강권 추가</h3>

      <div class="form-row">
        <label>수강권 종류</label>
        <select v-model="memForm.type" @change="onMembershipTypeChange">
          <option value="Unlimited">Unlimited Class</option>
          <option value="10회권">10회권</option>
          <option value="20회권">20회권</option>
          <option :value="WEEKLY_PASS">주2회권</option>
        </select>
      </div>

      <div class="form-row">
        <label>시작일</label>
        <input
          v-model="memForm.start_date"
          type="date"
          @change="calculateEndDate"
        />
      </div>

      <div class="form-row">
        <label>기간</label>
        <!-- 주2회권은 주 단위: 시작한 주를 1주차로 친 N주차 일요일이 종료일(종료일은 손으로 고칠 수 있음) -->
        <select
          v-if="memForm.type === WEEKLY_PASS"
          v-model="memForm.duration_weeks"
          @change="calculateEndDate"
        >
          <option value="">직접 입력</option>
          <option v-for="w in WEEKLY_PASS_WEEKS" :key="w" :value="String(w)">
            {{ w }}주
          </option>
        </select>
        <select
          v-else
          v-model="memForm.duration_days"
          @change="calculateEndDate"
        >
          <option value="">직접 입력</option>
          <option value="30">30일</option>
          <option value="60">60일</option>
          <option value="90">90일</option>
          <option value="180">180일</option>
          <option value="365">365일</option>
        </select>
      </div>
      <p v-if="memForm.type === WEEKLY_PASS" class="form-hint">
        {{ WEEKLY_PASS_HINT }}
      </p>

      <div class="form-row">
        <label>종료일</label>
        <input
          v-model="memForm.end_date"
          type="date"
          @input="onEndDateManualInput"
        />
      </div>

      <div class="form-row">
        <label>결제 수단</label>
        <select v-model="memForm.payment_method">
          <option value="card">카드</option>
          <option value="transfer">계좌이체</option>
          <option value="cash">현금</option>
        </select>
      </div>

      <div class="form-row">
        <label>결제 금액</label>
        <input v-model="memForm.price" type="number" min="0" />
      </div>

      <div class="form-row">
        <label>특이사항 (선택)</label>
        <textarea
          v-model="memForm.note"
          rows="3"
          :maxlength="MEMBERSHIP_NOTE_MAX"
          placeholder="예: 지인 할인, 다른 수강권에서 옮김"
        />
      </div>

      <div class="modal-actions">
        <button class="btn" @click="closeMembershipModal">취소</button>
        <button
          class="btn btn-primary"
          :disabled="modalSaving"
          @click="submitMembership"
        >
          완료
        </button>
      </div>
    </div>
  </div>

  <!-- 회원권 수정 모달 -->
  <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
    <div class="modal" @click.stop>
      <h3 class="modal-title">회원권 수정</h3>

      <div class="form-row">
        <label>수강권 종류</label>
        <input
          :value="editForm.type"
          type="text"
          disabled
          class="disabled-input"
        />
      </div>

      <div class="form-row">
        <label>상태</label>
        <select v-model="editForm.status">
          <option value="active">활성</option>
          <option value="expired">만료</option>
          <option value="cancelled">취소</option>
        </select>
      </div>

      <div class="form-row">
        <label>시작일</label>
        <input v-model="editForm.start_date" type="date" />
      </div>

      <div class="form-row">
        <label>만료일</label>
        <input v-model="editForm.end_date" type="date" />
      </div>

      <div v-if="editForm.total_count !== -1" class="form-row">
        <label>남은 횟수</label>
        <input
          v-model.number="editForm.remaining_count"
          type="number"
          min="0"
        />
      </div>

      <div class="form-row">
        <label>특이사항</label>
        <textarea
          v-model="editForm.note"
          rows="3"
          :maxlength="MEMBERSHIP_NOTE_MAX"
          placeholder="없음"
        />
      </div>

      <div class="modal-actions">
        <button class="btn" @click="closeEditModal">취소</button>
        <button
          class="btn btn-primary"
          :disabled="modalSaving"
          @click="submitEdit"
        >
          저장
        </button>
      </div>
    </div>
  </div>

  <!-- 휴회 생성 모달 -->
  <div v-if="showHoldModal" class="modal-overlay" @click="closeHoldModal">
    <div class="modal" @click.stop>
      <h3 class="modal-title">휴회 등록</h3>

      <div class="form-row">
        <label>시작일</label>
        <input v-model="holdForm.start_date" type="date" />
      </div>

      <div class="form-row">
        <label>종료일</label>
        <input v-model="holdForm.end_date" type="date" />
      </div>

      <div v-if="holdFormDays > 0" class="form-row">
        <label>휴회 일수</label>
        <span class="hold-days-display">{{ holdFormDays }}일</span>
      </div>

      <div class="form-row">
        <label>사유 (선택)</label>
        <input
          v-model="holdForm.reason"
          type="text"
          placeholder="예: 부상, 여행 등"
        />
      </div>

      <div class="modal-actions">
        <button class="btn" @click="closeHoldModal">취소</button>
        <button
          class="btn btn-primary"
          :disabled="modalSaving"
          @click="submitHold"
        >
          등록
        </button>
      </div>
    </div>
  </div>

  <!-- 휴회 수정 모달 -->
  <div
    v-if="showEditHoldModal"
    class="modal-overlay"
    @click="closeEditHoldModal"
  >
    <div class="modal" @click.stop>
      <h3 class="modal-title">휴회 수정</h3>

      <div class="form-row">
        <label>시작일</label>
        <input v-model="editHoldForm.start_date" type="date" />
      </div>

      <div class="form-row">
        <label>종료일</label>
        <input v-model="editHoldForm.end_date" type="date" />
      </div>

      <div v-if="editHoldFormDays > 0" class="form-row">
        <label>휴회 일수</label>
        <span class="hold-days-display">{{ editHoldFormDays }}일</span>
      </div>

      <div class="form-row">
        <label>사유 (선택)</label>
        <input
          v-model="editHoldForm.reason"
          type="text"
          placeholder="예: 부상, 여행 등"
        />
      </div>

      <div class="modal-actions">
        <button class="btn" @click="closeEditHoldModal">취소</button>
        <button
          class="btn btn-primary"
          :disabled="modalSaving"
          @click="submitEditHold"
        >
          저장
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { apiJson } from "@/lib/api";
import {
  daysLeftText,
  daysLeftTone,
  dotDate,
  prettyPhone,
  membershipStateLabel,
  membershipCountText,
  saveAdminNote,
  confirmDiscardDraft,
} from "@/lib/memberNotes";
import MemberNotes from "@/components/MemberNotes.vue";
import MemberTrialHistory from "@/components/trials/MemberTrialHistory.vue";
import { membershipEditBody, MEMBERSHIP_NOTE_MAX } from "@/lib/membershipEdit";
import { classTypeTone, itemTypeLabel } from "@/lib/classTypes";
import { float4Number } from "@/lib/float4";
import {
  WEEKLY_PASS,
  WEEKLY_PASS_WEEKS,
  WEEKLY_PASS_HINT,
  weeklyPassEndDate,
  weeklyPassNote,
  canExtendWeek,
  extendWeekConfirmText,
  extendWeekBody,
} from "@/lib/weeklyPass";

// cloudapi util.DefaultWeightKg와 같은 값 (남 75 / 여 55 / 그 외 65). 저장하지 않고 표시·계산에만 쓴다.
const DEFAULT_WEIGHT_KG = { male: 75, female: 55 };
const DEFAULT_WEIGHT_KG_UNKNOWN = 65;
// 회원 상세 카드를 그리기 전에 수강권 목록·휴회 이력을 기다리는 가장 긴 시간(bodyReady). 그보다 늦으면 프로필만 읽고 그린다
const BODY_WAIT_FOR_MEMBERSHIPS_MS = 1500;

// users.weight_kg(REAL)는 float4Number(@/lib/float4)로 되돌린다: 화면 글자와 [수정] 칸, [저장] 때 견주는 값이 모두 이 값이라 바꾸지
// 않은 몸무게는 PUT하지 않는다(출시 최종 확인 6차. 실시간 심박 화면과 같이 쓰려고 앱 전체 리뷰 1차에서 lib로 옮김)

// 오늘 날짜(KST) "YYYY-MM-DD". 브라우저 시간대와 상관없이 UTC+9로 센다(서버 util.NowInSeoul과 같은 날짜)
function kstToday() {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// "YYYY-MM-DD" 두 날짜의 일 수 차이(to − from). 날짜가 아니면 null
function daysBetween(from, to) {
  const toUtc = (s) => {
    const [y, m, d] = String(s || "")
      .slice(0, 10)
      .split("-")
      .map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const a = toUtc(from);
  const b = toUtc(to);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}

export default {
  name: "ProfileView",
  components: { MemberNotes, MemberTrialHistory },
  data() {
    return {
      WEEKLY_PASS,
      WEEKLY_PASS_WEEKS,
      WEEKLY_PASS_HINT,
      MEMBERSHIP_NOTE_MAX,
      profile: {
        name: "-",
        phone_number: "-",
        join_date: "-",
        join_date_fallback: "",
        app_signed_up: null,
        attendance: 0,
        absence: 0,
        max_hr: 0,
        admin_note: "",
        last_attendance: null, // 서버가 센 최근 출석 날짜(YYYY-MM-DD 또는 ""). 칸이 없는 옛 서버면 null
      },
      profileLoaded: false, // GET profile이 한 번이라도 성공했는지. 그 전에는 profile이 data() 기본값이다
      profileLoadFailed: false, // 첫 GET profile이 실패했는지 (특이사항 자리에 "불러오지 못함"을 보인다)
      todayKst: kstToday(), // 수강권 상태·D+n 계산 기준(KST). 프로필·회원권을 다시 읽을 때 새로 잡는다
      editingMaxHR: false,
      maxHRInput: 0,
      editingWeight: false,
      weightInput: 0,
      // 특이사항: 이 칸만 따로 고친다 (admin_note만 PUT)
      editingNote: false,
      noteDraft: "",
      noteOriginal: "", // [수정]을 누른 때의 특이사항. 바뀐 것이 없으면 보내지 않는다
      savingNote: false,
      // 기본 정보: 생년월일·성별·최대 심박수·몸무게
      editingProfile: false,
      savingProfile: false,
      profileEditOriginal: null, // [수정]을 누른 때의 profileEdit. 저장은 이 값에서 바꾼 칸만 보낸다
      profileEdit: {
        birth_date: "",
        gender: "",
        max_hr: 0,
        weight_kg: 0,
      },
      attendanceRecords: [],
      loadingAttendance: false,
      hasMoreAttendance: true,
      attendanceOffset: 0,
      // membership modal
      showMembershipModal: false,
      memForm: {
        type: "Unlimited",
        start_date: "",
        end_date: "",
        duration_days: "",
        duration_weeks: "", // 주2회권 기간(주)
        price: "",
        payment_method: "card",
        note: "", // 수강권 특이사항(선택, 2026-10-25)
      },
      isEndDateManual: false, // 종료일을 수동으로 입력 중인지 여부
      extendingId: "", // [한 주 연장]을 보내는 중인 수강권(두 번 누름 막기)
      // 대시보드
      dashboard: {
        totalZone4Plus: 0,
        weeklyZone4Plus: 0,
        minutesToSticker: 60,
      },
      // 회원권 목록
      memberships: [],
      loadingMemberships: false,
      membershipsSettled: false, // 첫 수강권 목록을 휴회 이력까지 읽었거나 못 읽었음(bodyReady)
      membershipsWaitExpired: false, // 수강권을 기다린 시간이 BODY_WAIT_FOR_MEMBERSHIPS_MS를 넘음(bodyReady)
      membershipsError: false, // 수강권 목록을 읽지 못함(다시 시도를 보인다)
      membershipsSeq: 0, // 활성/전체를 빠르게 바꿀 때 늦게 온 이전 응답을 버리기 위한 번호
      memTab: "active",
      // 회원권 추가·수정, 휴회 등록·수정 모달의 [완료]/[저장]/[등록] 요청 중. 두 번 누르면 수강권·휴회가 두 개 생겼다(4차 리뷰, main부터)
      modalSaving: false,
      // 회원권 수정 모달
      showEditModal: false,
      editForm: {
        id: "",
        type: "",
        total_count: 0,
        status: "",
        start_date: "",
        end_date: "",
        remaining_count: 0,
        note: "",
      },
      editOriginal: null, // 수정 모달에 채운 값 = 이 화면이 마지막으로 읽은 목록 값(모달을 열 때 다시 읽지 않음). 바꾼 칸만 보냄
      // 휴회 생성 모달
      showHoldModal: false,
      holdForm: {
        membership_id: "",
        start_date: "",
        end_date: "",
        reason: "",
      },
      // 휴회 수정 모달
      showEditHoldModal: false,
      editHoldForm: {
        id: "",
        start_date: "",
        end_date: "",
        reason: "",
      },
    };
  },
  created() {
    this.loadAll();
  },
  // 새로 고침·탭 닫기·주소 입력처럼 앱 밖으로 나갈 때도 쓰던 글이 있으면 브라우저가 묻는다(beforeRouteLeave는 앱 안 이동만 본다, 4차 리뷰)
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    clearTimeout(this.membershipsWaitTimer);
  },
  // 쓰던 특이사항·기본 정보·메모·연락 글이 있으면 다른 화면이나 다른 회원 상세로 옮기기 전에 묻는다(3차 리뷰: 신규회원 관리의 연락 기록
  // 모달은 묻는데 같은 글을 회원 상세에서 쓰면 '기록 보러 가기'·사이드메뉴·뒤로 가기에 말없이 사라졌다)
  beforeRouteLeave() {
    return this.confirmLeave();
  },
  beforeRouteUpdate(to, from) {
    if (to.params.user_id === from.params.user_id) return true;
    return this.confirmLeave();
  },
  watch: {
    // 다른 회원의 상세로 바로 옮겨 가면(방문 기록 목록에서 두 칸 이상 뒤로 등 /profile/B → /profile/A) vue-router가 이 화면을 다시 쓴다.
    // 예전에는 created()에서만 읽어 메모·연락 기록만 A로 바뀌고 머리·특이사항·수강권·출석은 B가 남았으며, 특이사항 [저장]과 [회원권 추가]는
    // 화면의 B가 아니라 주소의 A에게 썼다(2차 리뷰). 열린 수정 칸·모달을 닫고 처음부터 다시 읽는다. 다른 화면으로 나갈 때는 무시한다
    "$route.params.user_id"(id, oldId) {
      if (!id || id === oldId || this.$route.name !== "MemberProfile") return;
      this.resetForMember();
      this.loadAll();
    },
  },
  computed: {
    targetUserId() {
      return this.$route.params.user_id;
    },
    // 프로필을 읽었거나 읽지 못했음(그 전에는 특이사항 높이를 몰라 아래 카드를 그리지 않는다)
    profileSettled() {
      return this.profileLoaded || this.profileLoadFailed;
    },
    // 아래 카드(두 단·기본 정보·대시보드)를 그려도 되는지: 프로필과 첫 수강권 목록(휴회 이력까지)을 읽었거나 못 읽었을 때. 좁은 화면(≤ 1100px)은
    // 특이사항 → 수강권 → 메모·연락 순이라, 휴회 이력이나 수강권 목록이 프로필보다 늦게 오면 이미 보인 [+ 연락]이 44~190px 밀려 내려갔다
    // (5차 리뷰). 수강권이 아주 늦으면 BODY_WAIT_FOR_MEMBERSHIPS_MS까지만 기다린다
    bodyReady() {
      return (
        this.profileSettled &&
        (this.membershipsSettled || this.membershipsWaitExpired)
      );
    },
    // 보이는 특이사항은 앞뒤 공백을 뺀 값. 공백·빈 줄만 저장된 특이사항이 노란 테두리의 빈 칸으로 보이고, 앞의 빈 줄이 첫 줄을 밀었다
    // (4차 리뷰. 신규회원·회원관리 목록도 같은 값)
    noteText() {
      return (this.profile.admin_note || "").trim();
    },
    phoneText() {
      const p = this.profile.phone_number;
      if (!p || p === "-") return "";
      return prettyPhone(p);
    },
    // 가입일 = join_date(agreed_at). 앱 가입 전이면 서버가 주는 join_date_fallback(유저 생성일)
    joinDateYmd() {
      const jd = this.profile.join_date;
      if (jd && jd !== "-") return jd;
      return this.profile.join_date_fallback || "";
    },
    joinDateText() {
      return dotDate(this.joinDateYmd);
    },
    // 가입한 날 = D+0 (KST)
    joinDays() {
      if (!this.joinDateYmd) return null;
      const n = daysBetween(this.joinDateYmd, this.todayKst);
      return n === null ? null : Math.max(0, n);
    },
    // 최근 출석 날짜는 서버가 출석 수와 같은 예약에서 센다(last_attendance). 예전에는 출석 내역 첫 20줄(최근 순)에서 찾아, 출석 뒤 불참이
    // 20번 넘으면 "최근"이 사라지고 [더보기]를 눌러야 나왔다(3차 리뷰). 칸이 없는 옛 서버면 불러온 출석 내역에서 찾는다
    lastAttendedText() {
      if (!this.profileLoaded) return "";
      if (typeof this.profile.last_attendance === "string") {
        return dotDate(this.profile.last_attendance);
      }
      const rec = this.attendanceRecords.find((r) => r.attended);
      return rec ? dotDate(rec.date) : "";
    },
    // 수강권마다 상태(KST 오늘 기준): 만료(active 아님 또는 만료일 < 오늘) / 시작 예정(시작일 > 오늘) / 사용 중
    membershipRows() {
      const today = this.todayKst;
      const rows = this.memberships.map((mem) => {
        let state = "in_use";
        if (mem.status !== "active" || (mem.expired_at || "") < today) {
          state = "expired";
        } else if ((mem.start_date || "") > today) {
          state = "upcoming";
        }
        const daysLeft =
          state === "in_use" ? daysBetween(today, mem.expired_at) : null;
        return { mem, state, daysLeft };
      });
      // 사용 중(만료 빠른 순) → 시작 예정(시작 빠른 순) → 만료(최근 만료 먼저). API는 만든 순(최신 먼저)이라 재계약한 시작 예정
      // 수강권이 지금 쓰는 수강권 위에 왔다(2차 리뷰). 신규회원·회원관리 목록처럼 지금 쓰는 것을 먼저 보인다
      const rank = { in_use: 0, upcoming: 1, expired: 2 };
      const day = (v) => String(v || "").slice(0, 10);
      return rows.sort((a, b) => {
        if (a.state !== b.state) return rank[a.state] - rank[b.state];
        if (a.state === "upcoming") {
          return day(a.mem.start_date).localeCompare(day(b.mem.start_date));
        }
        if (a.state === "expired") {
          return day(b.mem.expired_at).localeCompare(day(a.mem.expired_at));
        }
        return day(a.mem.expired_at).localeCompare(day(b.mem.expired_at));
      });
    },
    holdFormDays() {
      if (!this.holdForm.start_date || !this.holdForm.end_date) return 0;
      const s = new Date(this.holdForm.start_date);
      const e = new Date(this.holdForm.end_date);
      const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
      return diff > 0 ? diff : 0;
    },
    editHoldFormDays() {
      if (!this.editHoldForm.start_date || !this.editHoldForm.end_date)
        return 0;
      const s = new Date(this.editHoldForm.start_date);
      const e = new Date(this.editHoldForm.end_date);
      const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
      return diff > 0 ? diff : 0;
    },
  },
  methods: {
    loadAll() {
      // 수강권이 아주 늦으면 그것만 기다리지 않고 카드를 그린다(bodyReady). 타이머는 data 밖에 두어 resetForMember가 지우지 않는다
      clearTimeout(this.membershipsWaitTimer);
      this.membershipsWaitTimer = setTimeout(() => {
        this.membershipsWaitExpired = true;
      }, BODY_WAIT_FOR_MEMBERSHIPS_MS);
      this.loadProfile();
      this.loadDashboard();
      this.loadAttendance();
      this.loadMemberships();
    },
    // 화면 상태를 data() 처음 값으로 되돌린다(다른 회원으로 옮길 때). 회원권 요청 번호만 이어 가 앞 회원의 늦은 응답을 버린다
    resetForMember() {
      const fresh = this.$options.data.call(this);
      fresh.membershipsSeq = this.membershipsSeq + 1;
      Object.assign(this.$data, fresh);
    },
    dotDate,
    membershipStateLabel,
    membershipCountText,
    // 남은 일 수 색: 회원관리 목록과 같은 규칙(lib/memberNotes, 1주 미만 빨강, 2주 미만 노랑)
    daysLeftTone,
    // 회원관리 목록과 같은 문구("내일 만료" 등, lib/memberNotes, 4차 리뷰)
    daysLeftText,
    weeklyPassNote,
    canExtendWeek,
    defaultWeightKg(gender) {
      return DEFAULT_WEIGHT_KG[gender] ?? DEFAULT_WEIGHT_KG_UNKNOWN;
    },
    openMembershipModal() {
      this.showMembershipModal = true;
      // 모달 열 때 폼 초기화
      this.memForm = {
        type: "Unlimited",
        start_date: "",
        end_date: "",
        duration_days: "",
        duration_weeks: "",
        price: "",
        payment_method: "card",
        note: "",
      };
      this.isEndDateManual = false;
    },
    closeMembershipModal() {
      this.showMembershipModal = false;
    },
    // 종류를 바꾸면 고른 기간(일·주)을 비운다. 일 단위 기간이 주2회권에 남거나 그 반대가 되지 않게
    onMembershipTypeChange() {
      this.memForm.duration_days = "";
      this.memForm.duration_weeks = "";
    },
    calculateEndDate() {
      // 주2회권: 시작한 주를 1주차로 친 N주차 일요일
      if (this.memForm.type === WEEKLY_PASS) {
        if (this.memForm.duration_weeks && this.memForm.start_date) {
          this.memForm.end_date = weeklyPassEndDate(
            this.memForm.start_date,
            Number(this.memForm.duration_weeks)
          );
          this.isEndDateManual = false;
        }
        return;
      }
      // 기간이 선택되어 있고 시작일이 있으면 자동 계산
      if (this.memForm.duration_days && this.memForm.start_date) {
        const startDate = new Date(this.memForm.start_date);
        const days = parseInt(this.memForm.duration_days);

        // 시작일부터 지정된 일수만큼 더하기 (시작일 포함이므로 -1일)
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + days - 1);

        // YYYY-MM-DD 형식으로 변환
        const year = endDate.getFullYear();
        const month = String(endDate.getMonth() + 1).padStart(2, "0");
        const day = String(endDate.getDate()).padStart(2, "0");
        this.memForm.end_date = `${year}-${month}-${day}`;
        this.isEndDateManual = false;
      }
    },
    onEndDateManualInput() {
      // 종료일을 수동으로 입력하면 기간 선택을 초기화
      this.isEndDateManual = true;
      if (this.memForm.end_date) {
        this.memForm.duration_days = "";
        this.memForm.duration_weeks = "";
      }
    },
    async submitMembership() {
      if (this.modalSaving) return;
      this.modalSaving = true;
      try {
        const body = {
          type: this.memForm.type,
          start_date: this.memForm.start_date,
          end_date: this.memForm.end_date,
          price: Number(this.memForm.price || 0),
          payment_method: this.memForm.payment_method,
          note: (this.memForm.note || "").trim(),
        };
        await apiJson(`/v1/admin/members/${this.targetUserId}/memberships`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        this.closeMembershipModal();
        this.loadProfile();
        this.loadMemberships();
      } catch (e) {
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
      } finally {
        this.modalSaving = false;
      }
    },
    goToForgotPassword() {
      this.$router.push("/forgot-password");
    },
    async loadProfile() {
      const userId = this.targetUserId;
      try {
        // 관리자가 특정 회원의 프로필을 조회
        const data = await apiJson(`/v1/admin/members/${userId}/profile`);
        // 그사이 다른 회원으로 옮겼으면 앞 회원의 늦은 응답은 버린다
        if (userId !== this.targetUserId) return;
        this.profile = {
          name: data.name || "-",
          phone_number: data.phone_number || "-",
          join_date: data.join_date || "-",
          // 가입일(agreed_at)이 없을 때 쓰는 유저 생성일과 앱 가입 여부(설계 3절). 칸이 없는 옛 서버면 배지를 보이지 않는다
          join_date_fallback: data.join_date_fallback || "",
          app_signed_up:
            typeof data.app_signed_up === "boolean" ? data.app_signed_up : null,
          attendance: data.attendance || 0,
          absence: data.absence || 0,
          max_hr: data.max_hr || 0,
          weight_kg: float4Number(data.weight_kg),
          // 몸무게가 없으면 서버가 true를 보낸다(계산에는 성별 기본값). 예전에는 이 칸을 옮기지 않아 "미입력 (기본 …kg 적용)" 대신
          // "- kg"이 보였다(출시 전부터, 출시 최종 확인 6차)
          weight_kg_is_default: data.weight_kg_is_default === true,
          birth_date: data.birth_date || "",
          gender: data.gender || "",
          admin_note: data.admin_note || "",
          last_attendance:
            typeof data.last_attendance === "string"
              ? data.last_attendance
              : null,
        };
        this.todayKst = kstToday();
        this.profileLoaded = true;
        this.profileLoadFailed = false;
      } catch (error) {
        if (userId !== this.targetUserId) return;
        console.error("Failed to load profile:", error);
        // 한 번 읽은 뒤의 다시 읽기 실패는 이전 화면을 그대로 둔다
        if (!this.profileLoaded) this.profileLoadFailed = true;
      }
    },
    async loadAttendance() {
      const userId = this.targetUserId;
      try {
        this.loadingAttendance = true;
        const data = await apiJson(
          `/v1/admin/members/${userId}/attendance?offset=0&limit=20`
        );
        if (userId !== this.targetUserId) return;
        this.attendanceRecords = data.records || [];
        this.hasMoreAttendance = data.has_more || false;
        this.attendanceOffset = 20;
      } catch (error) {
        if (userId !== this.targetUserId) return;
        console.error("Failed to load attendance:", error);
        this.attendanceRecords = [];
        this.hasMoreAttendance = false;
      } finally {
        if (userId === this.targetUserId) this.loadingAttendance = false;
      }
    },
    async loadMoreAttendance() {
      if (this.loadingAttendance || !this.hasMoreAttendance) return;

      const userId = this.targetUserId;
      try {
        this.loadingAttendance = true;
        const data = await apiJson(
          `/v1/admin/members/${userId}/attendance?offset=${this.attendanceOffset}&limit=20`
        );
        if (userId !== this.targetUserId) return;
        this.attendanceRecords = [
          ...this.attendanceRecords,
          ...(data.records || []),
        ];
        this.hasMoreAttendance = data.has_more || false;
        this.attendanceOffset += 20;
      } catch (error) {
        console.error("Failed to load more attendance:", error);
      } finally {
        if (userId === this.targetUserId) this.loadingAttendance = false;
      }
    },
    onScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        this.loadMoreAttendance();
      }
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
      const weekday = weekdays[date.getDay()];
      return `${month}월 ${day}일 (${weekday})`;
    },
    goToRecords(dateStr) {
      // RecordsView로 이동하면서 특정 주차 데이터 표시
      const date = new Date(dateStr);
      const weekOffset = this.calculateWeekOffset(date);
      this.$router.push({
        name: "MemberRecords",
        params: { user_id: this.targetUserId },
        query: { week: weekOffset },
      });
    },
    calculateWeekOffset(targetDate) {
      const now = new Date();
      const target = new Date(targetDate);

      // 현재 주의 시작일 (일요일)
      const nowWeekStart = new Date(now);
      nowWeekStart.setDate(now.getDate() - now.getDay());

      // 대상 날짜의 주 시작일
      const targetWeekStart = new Date(target);
      targetWeekStart.setDate(target.getDate() - target.getDay());

      // 주차 차이 계산
      const diffTime = nowWeekStart.getTime() - targetWeekStart.getTime();
      const diffWeeks = Math.floor(diffTime / (7 * 24 * 60 * 60 * 1000));

      return diffWeeks;
    },
    // R9 타입 색·표시명(lib/classTypes.js)
    getTypeClass(type) {
      return classTypeTone(type);
    },
    typeLabelOf(item) {
      return itemTypeLabel(item);
    },
    startEditMaxHR() {
      this.editingMaxHR = true;
      this.maxHRInput = this.profile.max_hr || 190;
    },
    cancelEditMaxHR() {
      this.editingMaxHR = false;
      this.maxHRInput = 0;
    },
    async loadDashboard() {
      const userId = this.targetUserId;
      try {
        const data = await apiJson(`/v1/admin/members/${userId}/dashboard`);
        if (userId !== this.targetUserId) return;
        this.dashboard = {
          totalZone4Plus: data.totalZone4Plus || 0,
          weeklyZone4Plus: data.weeklyZone4Plus || 0,
          minutesToSticker: data.minutesToSticker || 60,
        };
      } catch (e) {
        console.error("대시보드 로딩 실패:", e);
      }
    },
    statusLabel(status) {
      const map = { active: "활성", expired: "만료", cancelled: "취소" };
      return map[status] || status;
    },
    async loadMemberships() {
      // 전체는 휴회 조회가 더 많아 늦게 끝나므로, 전체 → 활성을 빠르게 누르면 전체 목록이 활성 탭을 덮었다. 마지막 요청만 반영한다
      const seq = ++this.membershipsSeq;
      try {
        this.loadingMemberships = true;
        this.membershipsError = false;
        const statusParam = this.memTab === "active" ? "active" : "all";
        const data = await apiJson(
          `/v1/admin/members/${this.targetUserId}/memberships?status=${statusParam}`
        );
        if (seq !== this.membershipsSeq) return;
        this.todayKst = kstToday();
        this.memberships = (data.memberships || []).map((mem) => ({
          ...mem,
          _holds: [],
        }));
        this.loadingMemberships = false;
        // 수강권 줄을 먼저 그리고 휴회 이력은 한꺼번에(병렬로) 읽어 채운다. 예전에는 수강권마다 휴회 이력을 하나씩 기다린 뒤에야 목록을 그려,
        // 좁은 화면(≤ 1100px)에서 먼저 그려진 메모·연락 카드가 0.3~0.5초 뒤 수강권 카드에 밀려 200~380px 내려갔다(3차 리뷰)
        await Promise.all(
          this.memberships.map(async (mem) => {
            try {
              const holdData = await apiJson(
                `/v1/admin/memberships/${mem.id}/holds`
              );
              if (seq === this.membershipsSeq)
                mem._holds = holdData.holds || [];
            } catch {
              // 휴회 이력을 못 읽으면 수강권 줄만 보인다
            }
          })
        );
      } catch (e) {
        if (seq !== this.membershipsSeq) return;
        console.error("회원권 목록 로딩 실패:", e);
        // 못 읽은 목록을 "수강권 0개 · 수강권이 없습니다"로 보이지 않는다(3차 리뷰). 실패 문구와 [다시 시도]
        this.memberships = [];
        this.membershipsError = true;
      } finally {
        if (seq === this.membershipsSeq) {
          this.loadingMemberships = false;
          this.membershipsSettled = true;
        }
      }
    },
    openEditModal(mem) {
      this.editForm = {
        id: mem.id,
        type: mem.type,
        total_count: mem.total_count,
        status: mem.status,
        start_date: mem.start_date,
        end_date: mem.expired_at,
        remaining_count: mem.remaining_count,
        note: mem.note || "",
      };
      this.editOriginal = { ...this.editForm };
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    async submitEdit() {
      if (this.modalSaving) return;
      this.modalSaving = true;
      try {
        // 관리자가 바꾼 칸만 보낸다: 목록을 읽은 뒤 보상·예약 등이 바꾼 다른 칸을 옛 값으로 덮지 않게
        const body = membershipEditBody(this.editOriginal, this.editForm);
        await apiJson(`/v1/admin/memberships/${this.editForm.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        this.closeEditModal();
        this.loadMemberships();
        this.loadProfile();
      } catch (e) {
        // 에러는 handleApiError에서 처리
      } finally {
        this.modalSaving = false;
      }
    },
    // 휴회 생성
    openHoldModal(mem) {
      this.holdForm = {
        membership_id: mem.id,
        start_date: "",
        end_date: "",
        reason: "",
      };
      this.showHoldModal = true;
    },
    closeHoldModal() {
      this.showHoldModal = false;
    },
    async submitHold() {
      if (this.modalSaving) return;
      this.modalSaving = true;
      try {
        await apiJson(
          `/v1/admin/memberships/${this.holdForm.membership_id}/holds`,
          {
            method: "POST",
            body: JSON.stringify({
              start_date: this.holdForm.start_date,
              end_date: this.holdForm.end_date,
              reason: this.holdForm.reason,
            }),
          }
        );
        this.closeHoldModal();
        this.loadMemberships();
        this.loadProfile();
      } catch (e) {
        // 에러는 handleApiError에서 처리
      } finally {
        this.modalSaving = false;
      }
    },
    // 휴회 수정
    openEditHoldModal(hold) {
      this.editHoldForm = {
        id: hold.id,
        start_date: hold.start_date,
        end_date: hold.end_date,
        reason: hold.reason || "",
      };
      this.showEditHoldModal = true;
    },
    closeEditHoldModal() {
      this.showEditHoldModal = false;
    },
    async submitEditHold() {
      if (this.modalSaving) return;
      this.modalSaving = true;
      try {
        await apiJson(`/v1/admin/holds/${this.editHoldForm.id}`, {
          method: "PUT",
          body: JSON.stringify({
            start_date: this.editHoldForm.start_date,
            end_date: this.editHoldForm.end_date,
            reason: this.editHoldForm.reason,
          }),
        });
        this.closeEditHoldModal();
        this.loadMemberships();
        this.loadProfile();
      } catch (e) {
        // 에러는 handleApiError에서 처리
      } finally {
        this.modalSaving = false;
      }
    },
    // 주2회권 [한 주 연장]: 종료일 +7일, 늘어난 주는 1회(서버가 정함). 자동으로는 하지 않고 코치가 누를 때만
    // 확인 창에 보인 종료일을 함께 보내(서버가 다르면 409) 같은 요청이 두 번 와도 한 주만 는다. 응답으로 그 줄을 바로 고치고,
    // 목록을 다시 읽을 때까지 버튼을 막아 다음 확인 창이 옛 종료일로 뜨지 않게 한다
    async extendWeek(mem) {
      if (this.extendingId || !confirm(extendWeekConfirmText(mem))) return;
      this.extendingId = mem.id;
      try {
        const updated = await apiJson(
          `/v1/admin/memberships/${mem.id}/extend-week`,
          { method: "POST", body: JSON.stringify(extendWeekBody(mem)) }
        );
        if (updated && updated.id === mem.id) Object.assign(mem, updated);
        this.loadProfile();
      } catch (e) {
        // 에러(409 종료일이 바뀜 등)는 handleApiError에서 알린다. 아래에서 목록을 다시 읽는다
      } finally {
        await this.loadMemberships();
        this.extendingId = "";
      }
    },
    // 휴회 삭제
    async deleteHold(hold) {
      if (
        !confirm(
          `휴회 (${dotDate(hold.start_date)} ~ ${dotDate(
            hold.end_date
          )})를 삭제하시겠습니까? 만료일이 ${hold.days}일 줄어듭니다.`
        )
      )
        return;
      try {
        await apiJson(`/v1/admin/holds/${hold.id}`, { method: "DELETE" });
        this.loadMemberships();
        this.loadProfile();
      } catch (e) {
        // 에러는 handleApiError에서 처리
      }
    },
    async saveMaxHR() {
      if (this.maxHRInput < 100 || this.maxHRInput > 250) {
        alert("최대 심박수는 100~250 사이의 값이어야 합니다.");
        return;
      }

      try {
        await apiJson(`/v1/admin/members/${this.targetUserId}/max-hr`, {
          method: "PUT",
          body: JSON.stringify({
            max_hr: this.maxHRInput,
          }),
        });

        this.profile.max_hr = this.maxHRInput;
        this.editingMaxHR = false;
      } catch (error) {
        console.error("최대 심박수 수정 실패:", error);
      }
    },
    startEditWeight() {
      this.editingWeight = true;
      this.weightInput = this.profile.weight_kg || 65;
    },
    cancelEditWeight() {
      this.editingWeight = false;
      this.weightInput = 0;
    },
    async saveWeight() {
      if (this.weightInput < 20 || this.weightInput > 300) {
        alert("몸무게는 20~300kg 사이의 값이어야 합니다.");
        return;
      }

      try {
        await apiJson(`/v1/admin/members/${this.targetUserId}/weight`, {
          method: "PUT",
          body: JSON.stringify({
            weight_kg: this.weightInput,
          }),
        });

        this.profile.weight_kg = this.weightInput;
        this.profile.weight_kg_is_default = false;
        this.editingWeight = false;
      } catch (error) {
        console.error("몸무게 수정 실패:", error);
      }
    },
    formatBirthDate(bd) {
      if (!bd || bd.length !== 8) return bd || "";
      return `${bd.slice(0, 4)}-${bd.slice(4, 6)}-${bd.slice(6, 8)}`;
    },
    genderLabel(g) {
      if (g === "male") return "남성";
      if (g === "female") return "여성";
      return "-";
    },
    // ---------------- 특이사항 ----------------
    startEditNote() {
      // 읽지 못한 프로필은 수정하지 않는다(버튼도 꺼짐). 화면 기본값("")을 저장하면 회원의 특이사항을 비운다
      if (!this.profileLoaded) return;
      this.noteOriginal = this.profile.admin_note || "";
      // 칸에는 보이는 값(앞뒤 공백·빈 줄을 뺀 값)을 넣는다. saveNote·noteHasDraft는 다듬은 값끼리 견준다
      this.noteDraft = this.noteOriginal.trim();
      this.editingNote = true;
      this.$nextTick(() => {
        const ta = this.$refs.noteTextarea;
        if (!ta) return;
        // 긴 특이사항은 칸을 글 높이만큼(화면 높이의 60%까지) 늘리고 맨 앞부터 보인다. 예전에는 4줄 칸이 커서를 따라 끝으로 스크롤되어
        // 가장 중요한 첫 줄(부상 등)이 가려졌다(3차 리뷰)
        if (ta.scrollHeight > ta.clientHeight) {
          const max = Math.round(window.innerHeight * 0.6);
          ta.style.height = `${Math.min(ta.scrollHeight + 2, max)}px`;
        }
        ta.setSelectionRange(0, 0);
        ta.focus();
        ta.scrollTop = 0;
      });
    },
    cancelEditNote() {
      this.editingNote = false;
      this.noteDraft = "";
    },
    // [취소]: 바꾼 내용이 있으면 연락 기록 모달처럼 한 번 묻는다
    requestCancelNote() {
      if (this.noteHasDraft() && !confirmDiscardDraft()) return;
      this.cancelEditNote();
    },
    noteHasDraft() {
      return (
        this.editingNote &&
        (this.noteDraft || "").trim() !== (this.noteOriginal || "").trim()
      );
    },
    // 기본 정보 수정 칸에서 [수정] 때 값과 다른 칸이 있는지
    profileHasDraft() {
      const orig = this.profileEditOriginal;
      if (!this.editingProfile || !orig) return false;
      const cur = this.profileEdit;
      const num = (v) => Number(v) || 0;
      return (
        (cur.birth_date || "") !== (orig.birth_date || "") ||
        (cur.gender || "") !== (orig.gender || "") ||
        num(cur.max_hr) !== num(orig.max_hr) ||
        num(cur.weight_kg) !== num(orig.weight_kg)
      );
    },
    // 이 화면에 저장하지 않은 글이 있는지: 특이사항·기본 정보 수정 칸, 메모·연락 폼(MemberNotes)
    hasUnsavedEdits() {
      const notes = this.$refs.memberNotes;
      return (
        this.noteHasDraft() ||
        this.profileHasDraft() ||
        !!(notes && notes.hasDraft())
      );
    },
    // 화면을 떠나도 되는지(쓰던 글이 있으면 묻는다)
    confirmLeave() {
      return !this.hasUnsavedEdits() || confirmDiscardDraft();
    },
    onBeforeUnload(event) {
      if (!this.hasUnsavedEdits()) return;
      event.preventDefault();
      event.returnValue = "";
    },
    async saveNote() {
      if (!this.profileLoaded || this.savingNote) return;
      // admin_note만 보낸다(서버는 보낸 칸만 쓴다). [수정] 때 값에서 바뀌지 않았으면 보내지 않아, 그 사이 다른 코치가 고친 값을 덮지 않는다.
      // 두 코치가 같이 고치면 늦게 저장한 내용이 남는다(설계 0절)
      // 칸에는 다듬은 값이 들어가므로 다듬은 값끼리 견준다: 앞뒤 공백이 붙어 저장된 특이사항을 손대지 않고 [저장]해도 보내지 않는다(5차
      // 리뷰: 서버 값 그대로와 견줘 그 사이 다른 코치가 고친 값을 옛 값으로 덮었다). 공백·빈 줄만 있던 특이사항도 같다: 화면에는 이미
      // "특이사항 없음"이라(noteText) 지울 것이 없다. 예전에는 이때 ""를 보내 그 사이 다른 코치가 쓴 특이사항을 지웠다(6차 리뷰)
      const note = (this.noteDraft || "").trim();
      if (note === (this.noteOriginal || "").trim()) {
        this.cancelEditNote();
        return;
      }
      this.savingNote = true;
      const userId = this.targetUserId;
      try {
        await saveAdminNote(userId, note);
        // 저장하는 사이 다른 회원으로 옮겼으면 그 회원 화면은 건드리지 않는다
        if (userId !== this.targetUserId) return;
        this.profile.admin_note = note;
        this.cancelEditNote();
      } catch (error) {
        // 실패 문구는 apiJson의 전역 오류 처리. 쓰던 내용은 그대로 둔다
        console.error("특이사항 저장 실패:", error);
      } finally {
        this.savingNote = false;
      }
    },
    // ---------------- 기본 정보 ----------------
    startEditProfile() {
      // 읽지 못한 프로필은 수정하지 않는다(버튼도 꺼짐). 예전에는 GET이 실패하거나 끝나기 전에 [수정]을 누르면 data() 기본값(생년월일·
      // 특이사항 없음)이 수정 칸에 들어가 저장 때 회원의 생년월일·특이사항을 비웠다(출시 최종 확인 3차)
      if (!this.profileLoaded) return;
      const bd = this.profile.birth_date || "";
      const bdIso =
        bd.length === 8
          ? `${bd.slice(0, 4)}-${bd.slice(4, 6)}-${bd.slice(6, 8)}`
          : "";
      this.profileEdit = {
        birth_date: bdIso,
        gender: this.profile.gender || "",
        max_hr: this.profile.max_hr || 0,
        weight_kg: this.profile.weight_kg || 0,
      };
      this.profileEditOriginal = { ...this.profileEdit };
      this.editingProfile = true;
    },
    cancelEditProfile() {
      this.editingProfile = false;
    },
    requestCancelProfile() {
      if (this.profileHasDraft() && !confirmDiscardDraft()) return;
      this.cancelEditProfile();
    },
    async saveProfileFields() {
      if (!this.profileLoaded || !this.profileEditOriginal) return;
      if (this.savingProfile) return;
      const bd = (this.profileEdit.birth_date || "").replace(/-/g, "").trim();
      if (bd && !/^\d{8}$/.test(bd)) {
        alert("생년월일이 올바르지 않습니다.");
        return;
      }
      const mh = Number(this.profileEdit.max_hr) || 0;
      const wk = Number(this.profileEdit.weight_kg) || 0;
      if (mh && (mh < 100 || mh > 250)) {
        alert("최대 심박수는 100~250 사이여야 합니다.");
        return;
      }
      // 서버는 최대 심박수를 정수로만 받는다(185.5는 400). 예전에는 화면 검사를 지나 앞의 프로필 PUT만 저장되고 여기서 멈췄다(출시 최종 확인 4차)
      if (mh && !Number.isInteger(mh)) {
        alert("최대 심박수는 정수로 입력해 주세요.");
        return;
      }
      if (wk && (wk < 20 || wk > 300)) {
        alert("몸무게는 20~300kg 사이여야 합니다.");
        return;
      }
      // 세 PUT을 차례로 보낸다(출시 최종 확인 1차). 예전에는 Promise.all로 함께 보냈고, 서버가 회원 행 전체를 읽고 다시 써서
      // 늦게 끝난 요청이 다른 요청의 칸(특이사항·몸무게·최대 심박수)을 읽기 전 값으로 되돌렸다(응답은 모두 200이라 화면은 저장된 것처럼
      // 보임). 서버도 이제 보낸 칸만 쓰지만(UpdateAdminFields), 롤백한 옛 서버와 함께 돌 때도 잃지 않게 하나씩 보낸다.
      // 성공한 요청의 값은 바로 화면에 반영하고, 실패하면 거기서 멈춘다(실패 문구는 apiJson의 전역 오류 처리)
      // 세 PUT 모두 [수정]을 누른 때의 값(profileEditOriginal)에서 바꾼 칸만 보내고, 바꾼 칸이 없으면 보내지 않는다(출시 최종 확인 3차·4차).
      // 서버는 보낸 칸만 쓰므로 관리자가 보지 않은 칸을 덮지 않는다. 최대 심박수·몸무게도 [수정] 때 값과 견준다: 다시 읽은 this.profile과
      // 견주면 [수정]을 연 채 회원권·휴회 작업이 loadProfile을 부른 사이 다른 곳에서 바뀐 값을 관리자가 건드리지 않았는데도 되돌린다.
      // 성별은 고른 값이 있을 때만 보낸다. 서버는 성별 ""(비우기)를 400으로 거절한다(users.gender는 enum, 출시 최종 확인 2차).
      // 특이사항은 이 저장에 들어가지 않는다(특이사항 칸이 admin_note만 따로 저장)
      const orig = this.profileEditOriginal;
      const changeMaxHR = mh && mh !== (Number(orig.max_hr) || 0);
      const changeWeight = wk && wk !== (Number(orig.weight_kg) || 0);
      const profileBody = {};
      if (bd !== (orig.birth_date || "").replace(/-/g, "").trim()) {
        profileBody.birth_date = bd;
      }
      if (this.profileEdit.gender && this.profileEdit.gender !== orig.gender) {
        profileBody.gender = this.profileEdit.gender;
      }
      this.savingProfile = true;
      // 저장은 [저장]을 누른 회원에게 끝까지 보낸다. 그사이 다른 회원으로 옮겼으면(resetForMember) 그 회원 화면에는 반영하지 않는다
      const userId = this.targetUserId;
      const show = (patch) => {
        if (userId === this.targetUserId) Object.assign(this.profile, patch);
      };
      try {
        if (Object.keys(profileBody).length > 0) {
          await apiJson(`/v1/admin/members/${userId}/profile`, {
            method: "PUT",
            body: JSON.stringify(profileBody),
          });
          // 저장된 칸은 [수정] 때 값에도 옮긴다(출시 최종 확인 4차). 뒤 PUT이 실패해 창이 열린 채 남으면 다음 [저장]은 지금 서버 값과
          // 견준다. 예전에는 [수정] 때 값이 그대로라, 이미 저장된 칸을 [수정] 때 값으로 되돌리고 다시 저장하면 그 칸을 "바뀌지 않음"으로
          // 보고 보내지 않아 DB에 첫 저장 값이 남았다(오류 문구 없이 창이 닫힘)
          if ("birth_date" in profileBody) {
            show({ birth_date: bd });
            orig.birth_date = bd;
          }
          if (profileBody.gender) {
            show({ gender: profileBody.gender });
            orig.gender = profileBody.gender;
          }
        }
        if (changeMaxHR) {
          await apiJson(`/v1/admin/members/${userId}/max-hr`, {
            method: "PUT",
            body: JSON.stringify({ max_hr: mh }),
          });
          show({ max_hr: mh });
          orig.max_hr = mh;
        }
        if (changeWeight) {
          await apiJson(`/v1/admin/members/${userId}/weight`, {
            method: "PUT",
            body: JSON.stringify({ weight_kg: wk }),
          });
          show({ weight_kg: wk, weight_kg_is_default: false });
          orig.weight_kg = wk;
        }
        if (userId === this.targetUserId) this.editingProfile = false;
      } catch (error) {
        console.error("회원 정보 수정 실패:", error);
      } finally {
        this.savingProfile = false;
      }
    },
  },
};
</script>

<style scoped>
.profile-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

/* 카드 공통 */
.card {
  background: #151718;
  border-radius: 10px;
  padding: 24px;
  box-sizing: border-box;
  min-width: 0;
}

.card-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.card-actions {
  display: flex;
  gap: 8px;
}

.title-count {
  font-size: 13px;
  font-weight: 500;
  color: #8e8e8e;
  background: #0a0a0a;
  border-radius: 10px;
  padding: 1px 8px;
}

.add-membership-btn {
  background: #78ff47;
  color: #000;
  border: 0;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
}

/* 머리: 이름·전화번호·가입일 */
.profile-head {
  background: #151718;
  border-radius: 10px;
  padding: 20px 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.head-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 16px;
  min-width: 0;
}

.member-name {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 26px;
  line-height: 130%;
  letter-spacing: -0.02em;
  color: #fff;
  word-break: break-all;
}

.member-phone {
  font-size: 16px;
  color: #d8d8d8;
  font-variant-numeric: tabular-nums;
}

.member-join {
  font-size: 14px;
  color: #8e8e8e;
  font-variant-numeric: tabular-nums;
}

.join-days {
  color: #78ff47;
  font-weight: 600;
}

.head-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
}

/* "앱 가입 전"은 신규회원 목록의 배지(tone-muted)와 같은 회색. 노랑은 부재·첫 출석 전 같은 할 일 표시에 쓴다 */
.head-badge.muted {
  background: rgba(156, 163, 175, 0.1);
  color: #9ca3af;
}

/* 좌우 2단: 왼쪽(특이사항·메모/연락) 오른쪽(수강권·출석). 두 단의 높이는 따로 간다 */
.profile-columns {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 12px;
  align-items: start;
}

.profile-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

/* 체험 이력 자리: 보일 때는 카드가 바로 .profile-content의 칸이 되고(체험 이력이 없으면 빈 칸 간격도 생기지 않는다),
   bodyReady 전에는 v-show가 display: none으로 덮는다 */
.trial-history-slot {
  display: contents;
}

/* 특이사항 */
.note-card.has-note {
  box-shadow: inset 3px 0 0 #fbbf24;
}

.note-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  font-size: 13px;
  font-weight: 700;
}

/* 한국어는 낱말 중간에서 줄을 바꾸지 않는다(keep-all), 띄어쓰기 없는 긴 글은 어디서든 끊는다 */
.note-text {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: #fff;
  white-space: pre-wrap;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.note-placeholder {
  margin: 0;
  font-size: 14px;
  color: #6b7075;
}

/* 작은 버튼 (수정 / 저장 / 취소) */
.max-hr-save-btn,
.max-hr-cancel-btn {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s;
}

.max-hr-save-btn {
  background: #78ff47;
  color: #000;
}

.max-hr-cancel-btn {
  background: #333;
  color: #fff;
}

.max-hr-save-btn:hover:not(:disabled),
.max-hr-cancel-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.max-hr-save-btn:disabled,
.max-hr-cancel-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.edit-max-hr-btn {
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #333;
  border-radius: 4px;
  color: #8e8e8e;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-max-hr-btn:hover:not(:disabled) {
  border-color: #555;
  color: #fff;
}

.edit-max-hr-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #0a0a0a;
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  width: 480px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: 20px;
}

.modal-title {
  color: #fff;
  margin: 0 0 16px 0;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.form-row label {
  color: #8e8e8e;
  font-size: 14px;
}

.form-row input,
.form-row select,
.form-row textarea {
  background: #151718;
  color: #fff;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 10px 12px;
}

.form-row textarea {
  font: inherit;
  resize: vertical;
  line-height: 1.5;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.form-hint {
  margin: -4px 0 12px;
  color: #8e8e8e;
  font-size: 13px;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn {
  background: #333;
  color: #fff;
  border: 1px solid #555;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
}

.btn-primary {
  background: #78ff47;
  color: #000;
  border: 0;
}

/* 요청 중에는 다시 누를 수 없다 */
.btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* 대시보드 */
.dash-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.dash-item {
  background: #0a0a0a;
  border: 1px solid rgba(114, 114, 114, 0.2);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dash-label {
  color: #8e8e8e;
  font-size: 14px;
}

.dash-value {
  font-size: 28px;
  font-weight: 600;
}

.dash-value.purple {
  color: #8b5cf6;
}
.dash-value.orange {
  color: #f59e0c;
}
.dash-value.green {
  color: #22c55e;
}

.dash-unit {
  font-size: 14px;
  color: #8e8e8e;
  font-weight: 400;
  margin-left: 2px;
}

/* 수강권 */
.mem-tabs {
  display: flex;
  gap: 8px;
}

.mem-tab {
  padding: 6px 14px;
  border-radius: 5px;
  background: #1b1d1e;
  color: #8e8e8e;
  border: 0;
  cursor: pointer;
  font-size: 13px;
}

.mem-tab.active {
  background: #d8d8d8;
  color: #000;
  font-weight: 600;
}

.membership-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.membership-item {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 14px 18px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 12px;
}

/* 만료: 수강권 이름만 흐리게 하고 날짜·'만료'는 읽을 수 있게 둔다. 예전에는 줄 전체를 opacity 0.6으로 흐려 만료일 글자가 약 3:1이었다
   (재계약 연락에 필요한 것이 그 만료일이다, 2차 리뷰). 만료는 회색 점과 '만료' 표시로 가른다 */
.membership-item.state-expired .mem-type {
  color: #9d9d9d;
}

.mem-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.mem-type-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
}

.mem-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-sizing: border-box;
  flex-shrink: 0;
}

.mem-dot.in_use {
  background: #22c55e;
}

.mem-dot.upcoming {
  border: 2px solid #60a5fa;
}

.mem-dot.expired {
  background: #555;
}

.mem-type {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.mem-state,
.mem-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.mem-state.in_use {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.mem-state.upcoming {
  background: rgba(96, 165, 250, 0.15);
  color: #60a5fa;
}

.mem-state.expired {
  background: rgba(142, 142, 142, 0.15);
  color: #b0b0b0;
}

.mem-status.cancelled {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.mem-count {
  font-size: 14px;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.mem-details {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 14px;
  color: #8e8e8e;
  font-variant-numeric: tabular-nums;
}

.mem-days.safe {
  color: #22c55e;
}

.mem-week-note {
  color: #fbbf24;
}

/* 수강권 특이사항: 기간 아래 회색 줄(줄바꿈 그대로, 한국어 낱말 중간에서 줄을 바꾸지 않음) */
.mem-note {
  margin: 6px 0 0;
  color: #b4b4b4;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.edit-mem-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.mem-days.warning {
  color: #fbbf24;
}

.mem-days.danger {
  color: #ef4444;
}

.edit-mem-btn {
  padding: 6px 14px;
  background: transparent;
  border: 1px solid #333;
  border-radius: 5px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-mem-btn:hover {
  border-color: #78ff47;
  color: #78ff47;
}

.mem-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.hold-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid #222;
}

.hold-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  flex-wrap: wrap;
}

.hold-period {
  color: #f59e0c;
}

.hold-reason {
  color: #8e8e8e;
}

.hold-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.hold-edit-btn,
.hold-delete-btn {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid #333;
  background: transparent;
  color: #8e8e8e;
  transition: all 0.2s;
}

.hold-edit-btn:hover {
  border-color: #78ff47;
  color: #78ff47;
}

.hold-delete-btn:hover {
  border-color: #ef4444;
  color: #ef4444;
}

.hold-days-display {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.disabled-input {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 출석 */
.attendance-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-bottom: 14px;
  font-size: 14px;
  color: #8e8e8e;
  font-variant-numeric: tabular-nums;
}

.attendance-summary strong {
  color: #fff;
  font-weight: 600;
  margin-left: 2px;
}

.attendance-summary strong.attended {
  color: #78ff47;
}

.attendance-summary strong.absent {
  color: #ef4444;
}

.attendance-summary .sep {
  color: #555;
}

.attendance-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 8px;
}

.attendance-list::-webkit-scrollbar {
  width: 6px;
}

.attendance-list::-webkit-scrollbar-track {
  background: #2a2a2a;
  border-radius: 3px;
}

.attendance-list::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 3px;
}

.attendance-list::-webkit-scrollbar-thumb:hover {
  background: #777;
}

.attendance-item {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 72px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.attendance-indicator {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}

.attendance-indicator.attended {
  background: #78ff47;
}

.attendance-indicator.absent {
  background: #ef4444;
}

.attendance-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.attendance-date {
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #ffffff;
}

/* "10월 14일 (수)"와 "18:00"은 각각 한 줄. 모자라면 둘 사이에서만 줄을 바꾼다(요일이 날짜와 떨어졌다, 2차 리뷰) */
.att-day,
.att-time {
  white-space: nowrap;
}

.attendance-type {
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
  letter-spacing: -0.02em;
}

.attendance-type.hyb {
  color: #49de80;
}

.attendance-type.core {
  color: #f59e0c;
}

.attendance-type.car {
  color: #ef4444;
}

.attendance-action {
  padding: 0 14px;
  height: 34px;
  background: #ffffff;
  border: 1px solid #333333;
  border-radius: 5px;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 110%;
  text-align: center;
  letter-spacing: -0.02em;
  color: #000000;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  transition: opacity 0.2s;
}

.attendance-action:hover {
  opacity: 0.9;
}

/* 무한 스크롤 관련 스타일 */
.loading-more,
.end-message,
.empty-state {
  text-align: center;
  padding: 20px;
  color: #8e8e8e;
  font-size: 14px;
}

.empty-state p,
.loading-more p,
.end-message p {
  margin: 0;
}

.mem-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.mem-error p {
  color: #ef4444;
}

.load-more {
  text-align: center;
  padding: 12px;
}

.load-more-btn {
  background: #333;
  border: 1px solid #555;
  border-radius: 8px;
  padding: 10px 24px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-btn:hover {
  background: #444;
  border-color: #666;
}

/* 기본 정보 */
.extra-info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
.extra-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.extra-label {
  font-size: 12px;
  color: #8b9096;
  font-weight: 500;
}
.extra-value {
  font-size: 14px;
  color: #fff;
  padding: 10px 12px;
  background: #1c1f21;
  border-radius: 8px;
  min-height: 40px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  /* 한 줄의 칸이 같은 높이(몸무게 "미입력 (기본 55kg 적용)"이 두 줄이 되어도), 한국어 낱말 중간에서 줄을 바꾸지 않는다("적용"이 쪼개졌다) */
  flex: 1;
  word-break: keep-all;
  overflow-wrap: anywhere;
}
.extra-input {
  font-size: 14px;
  color: #fff;
  padding: 10px 12px;
  background: #1c1f21;
  border: 1px solid #3a3d3f;
  border-radius: 8px;
  outline: none;
  font-family: inherit;
  width: 100%;
  min-height: 40px;
  box-sizing: border-box;
}
.extra-input:focus {
  border-color: #78ff47;
}
textarea.extra-input {
  resize: vertical;
  line-height: 1.5;
  /* 쓰는 중에도 읽을 때(.note-text)처럼 한국어 낱말 중간에서 줄을 바꾸지 않는다(3차 리뷰) */
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* 사이드메뉴가 보이는 1101~1279px 화면은 2단 너비가 빠듯하므로 두 단을 같은 너비로 둔다(왼쪽 단이 5:7로 좁아 기록 수정 폼 버튼이 쪼개졌다) */
@media (max-width: 1279px) {
  .profile-columns {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

/* 같은 너비에서 오른쪽 단이 좁아진 만큼 출석 줄의 점·여백·버튼을 줄여 날짜와 시각을 한 줄에 둔다(2차 리뷰) */
@media (min-width: 1101px) and (max-width: 1279px) {
  .attendance-item {
    padding: 12px 10px;
    gap: 10px;
  }

  .attendance-indicator {
    width: 24px;
    height: 24px;
  }

  .attendance-action {
    padding: 0 8px;
  }
}

/* 좁은 화면(≤ 1100px): 한 줄로 특이사항 → 수강권 → 메모·연락 → 출석 → 기본 정보·대시보드 */
@media (max-width: 1100px) {
  .profile-columns {
    display: flex;
    flex-direction: column;
    /* 넓은 화면 그리드의 align-items: start가 남으면 카드가 내용 너비로 줄어든다 */
    align-items: stretch;
    gap: 12px;
  }

  .profile-col {
    display: contents;
  }

  .slot-note {
    order: 1;
  }

  .slot-mem {
    order: 2;
  }

  .slot-notes {
    order: 3;
  }

  .slot-att {
    order: 4;
  }

  .extra-info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .card,
  .profile-head {
    padding: 16px;
  }

  .member-name {
    font-size: 22px;
  }

  .dash-grid {
    grid-template-columns: 1fr;
  }

  .membership-item {
    padding: 12px 14px;
  }

  .attendance-item {
    padding: 12px 14px;
    gap: 12px;
  }

  .attendance-indicator {
    width: 28px;
    height: 28px;
  }
}

@media (max-width: 520px) {
  .extra-info-grid {
    grid-template-columns: 1fr;
  }

  .add-membership-btn {
    width: 100%;
  }
}
</style>
